import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config/api';

const DEFAULT_PREFERENCES = {
  dislikedIngredients: [],
  dietaryType: 'equilibrado',
  cuisineStyle: 'mixto',
  favorites: [],
  quickLunch: false,
  personCount: 1,
  planType: 'weekly',
  planDays: 7
};
const DIET_INFO = {
  equilibrado: 'Combina proteínas, vegetales y carbohidratos de forma balanceada.',
  saludable: 'Prioriza alimentos frescos, bajos en ultraprocesados y grasas saturadas.',
  'saludable-libre': 'Base saludable durante la semana con algunas comidas más flexibles.',
  'no-importa': 'No aplica restricciones, se prioriza variedad y practicidad.'
};
const CUISINE_INFO = {
  mixto: 'Combina recetas argentinas e internacionales para máxima variedad.',
  argentina: 'Prioriza cocina casera argentina y sabores tradicionales.',
  internacional: 'Mezcla platos globales de distintas cocinas del mundo.',
  mediterranea: 'Énfasis en verduras, aceite de oliva, pescado y legumbres.',
  mexicana: 'Incluye sabores especiados, tortillas, porotos y salsas.',
  asiatica: 'Prioriza salteados, bowls, soja, curry y técnicas orientales.'
};

function PreferencesPage({ userId, token }) {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [newDisliked, setNewDisliked] = useState('');
  const [newFavorite, setNewFavorite] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPreferences = useCallback(async () => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      setPreferences({ ...DEFAULT_PREFERENCES, ...JSON.parse(saved) });
    }

    try {
      const res = await fetch(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const backendPreferences = data?.user?.preferences;
        if (backendPreferences) {
          const merged = { ...DEFAULT_PREFERENCES, ...backendPreferences };
          setPreferences(merged);
          localStorage.setItem('userPreferences', JSON.stringify(merged));
        }
      }
    } catch (_) {
      // local fallback
    }
  }, [userId, token]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const addDisliked = () => {
    if (newDisliked.trim()) {
      setPreferences({
        ...preferences,
        dislikedIngredients: [...preferences.dislikedIngredients, newDisliked.trim()]
      });
      setNewDisliked('');
    }
  };

  const removeDisliked = (idx) => {
    const updated = preferences.dislikedIngredients.filter((_, i) => i !== idx);
    setPreferences({ ...preferences, dislikedIngredients: updated });
  };

  const addFavorite = () => {
    if (newFavorite.trim()) {
      setPreferences({
        ...preferences,
        favorites: [...preferences.favorites, newFavorite.trim()]
      });
      setNewFavorite('');
    }
  };

  const removeFavorite = (idx) => {
    const updated = preferences.favorites.filter((_, i) => i !== idx);
    setPreferences({ ...preferences, favorites: updated });
  };

  const savePreferences = async () => {
    try {
      const payload = {
        ...preferences,
        personCount: Math.max(1, parseInt(preferences.personCount, 10) || 1)
      };
      const res = await fetch(`${API_URL}/users/${userId}/preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error || 'Error al guardar preferencias');
        setSuccess('');
        return;
      }

      setPreferences(payload);
      localStorage.setItem('userPreferences', JSON.stringify(payload));
      setSuccess('Preferencias guardadas');
      setError('');
    } catch (err) {
      setError('Error: ' + err.message);
      setSuccess('');
    }
  };

  return (
    <div className="page preferences-page">
      <h2>Mis Preferencias</h2>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <div className="preference-section">
        <h3>Tipo de alimentación</h3>
        <select
          value={preferences.dietaryType}
          onChange={(e) => setPreferences({ ...preferences, dietaryType: e.target.value })}
        >
          <option value="equilibrado">Equilibrado</option>
          <option value="saludable">Saludable</option>
          <option value="saludable-libre">Saludable + Comidas Libres</option>
          <option value="no-importa">No Importa</option>
        </select>
        <p className="dietary-info">{DIET_INFO[preferences.dietaryType]}</p>
      </div>

      <div className="preference-section">
        <h3>Estilo de cocina</h3>
        <select
          value={preferences.cuisineStyle}
          onChange={(e) => setPreferences({ ...preferences, cuisineStyle: e.target.value })}
        >
          <option value="mixto">Mixto (Argentino + Internacional)</option>
          <option value="argentina">Argentina</option>
          <option value="internacional">Internacional</option>
          <option value="mediterranea">Mediterránea</option>
          <option value="mexicana">Mexicana</option>
          <option value="asiatica">Asiática</option>
        </select>
        <p className="dietary-info">{CUISINE_INFO[preferences.cuisineStyle]}</p>
      </div>

      <div className="preference-section">
        <h3>Personas para el menú</h3>
        <select
          value={preferences.personCount}
          onChange={(e) => setPreferences({ ...preferences, personCount: Number(e.target.value) })}
        >
          {[...Array(10)].map((_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1} persona(s)</option>
          ))}
        </select>
      </div>

      <div className="preference-section">
        <h3>Período del menú</h3>
        <select
          value={preferences.planType}
          onChange={(e) => setPreferences({ ...preferences, planType: e.target.value })}
        >
          <option value="weekly">Toda la semana (7 días)</option>
          <option value="current-week">Solo semana en curso (desde hoy)</option>
          <option value="month">Mes completo (30 días)</option>
          <option value="custom">Cantidad personalizada de días</option>
        </select>
        {preferences.planType === 'custom' && (
          <div style={{ marginTop: '10px' }}>
            <input
              type="number"
              min="1"
              max="31"
              value={preferences.planDays}
              onChange={(e) => setPreferences({
                ...preferences,
                planDays: Math.max(1, Math.min(31, Number(e.target.value) || 1))
              })}
            />
          </div>
        )}
      </div>

      <div className="preference-section">
        <h3>Ingredientes a evitar</h3>
        <div className="input-group">
          <input
            type="text"
            placeholder="Ej: tomate, lácteos"
            value={newDisliked}
            onChange={(e) => setNewDisliked(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addDisliked()}
          />
          <button onClick={addDisliked} className="btn-secondary">Agregar</button>
        </div>
        <div className="tags">
          {preferences.dislikedIngredients.map((item, idx) => (
            <span key={idx} className="tag">
              {item}
              <button onClick={() => removeDisliked(idx)}>x</button>
            </span>
          ))}
        </div>
      </div>

      <div className="preference-section">
        <h3>Comidas favoritas</h3>
        <div className="input-group">
          <input
            type="text"
            placeholder="Ej: pasta, milanesa"
            value={newFavorite}
            onChange={(e) => setNewFavorite(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addFavorite()}
          />
          <button onClick={addFavorite} className="btn-secondary">Agregar</button>
        </div>
        <div className="tags">
          {preferences.favorites.map((item, idx) => (
            <span key={idx} className="tag">
              {item}
              <button onClick={() => removeFavorite(idx)}>x</button>
            </span>
          ))}
        </div>
      </div>

      <div className="preference-section checkbox">
        <label>
          <input
            type="checkbox"
            checked={preferences.quickLunch}
            onChange={(e) => setPreferences({ ...preferences, quickLunch: e.target.checked })}
          />
          Necesito menú rápido al mediodía (viandas para llevar)
        </label>
      </div>

      <button onClick={savePreferences} className="btn-primary">
        Guardar preferencias
      </button>
    </div>
  );
}

export default PreferencesPage;
